import { scoreQueryOptions } from "../scores/scoreQueryOptions";
/**
 * The game detail Injuries tab: one selected team's report from
 * sports-api `/team/injuries-get`, each with its own independent empty
 * state (R3, and the v1 bug it fixes: one team with no injuries must not
 * blank the other). ISSUE-984.
 */
import { useTeamInjuriesGet } from "@sportsmet/sports-sdk/private";
import { SegmentedControl, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { InjuryList } from "../injuries/InjuryList";
import { TeamMark } from "./TeamMark";
import { resolvePlatePair } from "./teamPlateText";
import { GAME_SECTION_CARD_CLASS } from "./GameSectionCard";

export type typeInjuriesTabTeam = {
  teamId: string;
  name: string;
  abbreviation: string;
  logoUrl: string | null;
};

type typeTeamInjuriesRead = ReturnType<typeof useTeamInjuriesGet>;

function TeamSection(props: {
  team: typeInjuriesTabTeam;
  plateText: string;
  testId: string;
  read: typeTeamInjuriesRead;
}): ReactNode {
  const { read } = props;

  useEffect(() => {
    if (read.data?.data == null && extractErrorCode(read.data) !== null) {
      showApiError(read.data, undefined, read.dataUpdatedAt);
    }
  }, [read.dataUpdatedAt, props.team.teamId]);

  const hasError =
    read.data?.data == null && extractErrorCode(read.data) !== null;
  const data =
    read.data?.data?.teamId === props.team.teamId ? read.data.data : null;

  return (
    <div
      className={GAME_SECTION_CARD_CLASS + " p-3"}
      data-testid={props.testId}
    >
      <div className="flex items-center gap-2">
        <TeamMark
          name={props.team.name}
          abbreviation={props.plateText}
          logoUrl={props.team.logoUrl}
          size="md"
        />
        <span className="text-body-strong text-ink">{props.team.name}</span>
      </div>
      {hasError ? null : (
        <InjuryList
          hasReport={data?.hasReport ?? false}
          entries={data?.injuries ?? []}
          team={{
            teamId: props.team.teamId,
            name: props.team.name,
            abbreviation: props.team.abbreviation,
          }}
        />
      )}
    </div>
  );
}

export const InjuriesTab = ({
  home,
  away,
  selectedTeamId,
  onSelectTeam,
}: {
  home: typeInjuriesTabTeam;
  away: typeInjuriesTabTeam;
  selectedTeamId?: string | null;
  onSelectTeam?: (teamId: string) => void;
}): ReactNode => {
  const [localTeamId, setLocalTeamId] = useState<string | null>(null);
  const selected = selectedTeamId === undefined ? localTeamId : selectedTeamId;
  const isHome = selected === home.teamId;
  const team = isHome ? home : away;
  const awayRead = useTeamInjuriesGet(
    { teamId: away.teamId },
    { ...scoreQueryOptions, refetchInterval: 120_000 },
  );
  const homeRead = useTeamInjuriesGet(
    { teamId: home.teamId },
    { ...scoreQueryOptions, refetchInterval: 120_000 },
  );
  const read = isHome ? homeRead : awayRead;
  const { awayText, homeText } = resolvePlatePair({ away, home });
  return (
    <div className="flex flex-col gap-4" data-testid="injuries-tab">
      <SegmentedControl
        label="Choose injury report team"
        fullWidth
        testId="injuries-team-switch"
        value={team.teamId}
        options={[
          { value: away.teamId, label: awayText },
          { value: home.teamId, label: homeText },
        ]}
        onValueChange={(id) => {
          setLocalTeamId(id);
          onSelectTeam?.(id);
        }}
      />
      {read.isPending ? (
        <div data-testid="injuries-tab-loading">
          <Skeleton shape="card" />
        </div>
      ) : (
        <TeamSection
          key={team.teamId}
          team={team}
          plateText={isHome ? homeText : awayText}
          testId={isHome ? "injuries-team-home" : "injuries-team-away"}
          read={read}
        />
      )}
    </div>
  );
};
